import { createReducer } from '@reduxjs/toolkit';

import { TableItem } from '../types';
import {
	Sng,
	tournamentsActiveFailure,
	tournamentsActiveRequest,
	tournamentsActiveSuccess,
	tournamentsClear,
	tournamentsInviteOpen,
	tournamentsListRequest,
	tournamentsRegisterErrorClear,
	tournamentsRegisterFailure,
	tournamentsRegisterRequest,
	tournamentsRegisterSuccess,
	tournamentsSet,
	tournamentsUnregisterFailure,
	tournamentsUnregisterRequest,
	tournamentsUnregisterSuccess,
	tournamentsUpsert,
} from './tournamentsActions';

type ActiveRoomState = {
	loading: boolean;
	error: string | null;
	data: TableItem[];
};

export type TournamentsState = {
	items: Sng[];
	byMinLevel: Record<number, Sng>;
	listLoading: boolean;
	registerLoading: boolean;
	registerError: string | null;
	registerErrorTournamentId: string | null;
	inviteTournamentId?: string | null;
	inviteEntryLevel?: number | null;
	activeByRoom: Record<string, ActiveRoomState>;
};

const initialState: TournamentsState = {
	items: [],
	byMinLevel: {},
	listLoading: false,
	registerLoading: false,
	registerError: null,
	registerErrorTournamentId: null,
	inviteTournamentId: null,
	inviteEntryLevel: null,
	activeByRoom: {},
};

export const tournamentsReducer = createReducer(initialState, b => {
	b.addCase(tournamentsListRequest, s => {
		s.listLoading = true;
	});

	b.addCase(tournamentsSet, (s, a) => {
		s.items = a.payload;
		s.byMinLevel = Object.fromEntries(a.payload.map(t => [t.minLevel, t]));
		s.listLoading = false;
	});

	b.addCase(tournamentsUpsert, (s, a) => {
		const t = a.payload;
		s.byMinLevel[t.minLevel] = { ...(s.byMinLevel[t.minLevel] || {}), ...t };
		s.items = Object.values(s.byMinLevel);
	});

	b.addCase(tournamentsClear, s => {
		s.items = [];
		s.byMinLevel = {};
		s.listLoading = false;
		s.activeByRoom = {};
	});

	b.addCase(tournamentsRegisterRequest, s => {
		s.registerLoading = true;
		s.registerError = null;
		s.registerErrorTournamentId = null;
	});

	b.addCase(tournamentsRegisterSuccess, (s, a) => {
		s.registerLoading = false;
		s.registerError = null;
		s.registerErrorTournamentId = null;
		const { tournamentId } = a.payload;

		s.items = s.items.map(t =>
			String(t.id) === String(tournamentId) ? { ...t, registered: true } : t,
		);
	});

	b.addCase(tournamentsRegisterFailure, (s, a) => {
		s.registerLoading = false;
		s.registerError = a.payload.error;
		s.registerErrorTournamentId = a.payload.tournamentId;
	});

	b.addCase(tournamentsUnregisterRequest, s => {
		s.registerLoading = true;
		s.registerError = null;
		s.registerErrorTournamentId = null;
	});

	b.addCase(tournamentsUnregisterSuccess, (s, a) => {
		s.registerLoading = false;
		s.registerError = null;
		s.registerErrorTournamentId = null;
		const { tournamentId } = a.payload;

		s.items = s.items.map(t =>
			String(t.id) === String(tournamentId) ? { ...t, registered: false } : t,
		);
	});

	b.addCase(tournamentsUnregisterFailure, (s, a) => {
		s.registerLoading = false;
		s.registerError = a.payload.error;
		s.registerErrorTournamentId = a.payload.tournamentId;
	});

	b.addCase(tournamentsRegisterErrorClear, s => {
		s.registerError = null;
		s.registerErrorTournamentId = null;
	});

	b.addCase(tournamentsInviteOpen, (s, a) => {
		s.inviteTournamentId = a.payload.tournamentId;
		s.inviteEntryLevel = a.payload.entryLevel;
	});

	b.addCase(tournamentsActiveRequest, (s, a) => {
		const { roomId } = a.payload;

		s.activeByRoom[roomId] = {
			loading: true,
			error: null,
			data: s.activeByRoom[roomId]?.data ?? [],
		};
	});

	b.addCase(tournamentsActiveSuccess, (s, a) => {
		const { roomId, data } = a.payload;

		s.activeByRoom[roomId] = {
			loading: false,
			error: null,
			data,
		};
	});

	b.addCase(tournamentsActiveFailure, (s, a) => {
		const { roomId, error } = a.payload;

		s.activeByRoom[roomId] = {
			loading: false,
			error,
			data: s.activeByRoom[roomId]?.data ?? [],
		};
	});
});

// selectors
export const selectTournaments = (state: any) => state.tournaments.items;

export const selectTournamentsListLoading = (state: any) => state.tournaments.listLoading;

export const selectTournamentByMinLevel = (ml: number) => (state: any) =>
	state.tournaments.byMinLevel[ml];

export const selectInviteTournament = (state: any) => ({
	tournamentId: state.tournaments.inviteTournamentId,
	entryLevel: state.tournaments.inviteEntryLevel,
});

export const selectActiveTournamentsByRoom = (roomId: string) => (state: any) =>
	state.tournaments.activeByRoom[roomId] ?? {
		loading: false,
		error: null,
		data: [],
	};
